import {
  Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild, signal, computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

/**
 * Lightweight searchable dropdown for string option lists — a drop-in replacement
 * for a native <select> when the option count grows past the "scroll-and-scan"
 * threshold (Team / Manager / Employer / Location dropdowns on the Team Explorer
 * filter bar).
 *
 * Not a full ControlValueAccessor to keep the surface small; consumers use
 * [value]/(valueChange) alongside their existing state.
 */
@Component({
  selector: 'iskill-searchable-select',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './searchable-select.component.html',
  styleUrls: ['./searchable-select.component.scss'],
})
export class SearchableSelectComponent {
  /** Options are held in a signal so `filtered` recomputes when the parent
   *  hands over a new array (cascading filters, people reload). A plain
   *  @Input field is invisible to `computed`, which left the dropdown
   *  showing the previous list until the user typed in the search box. */
  private readonly optionsSig = signal<string[]>([]);
  @Input() set options(v: string[]) { this.optionsSig.set(v ?? []); }
  get options(): string[] { return this.optionsSig(); }
  /** Label shown when nothing selected, and used as the always-present "clear" row. */
  @Input() placeholder = 'All';
  @Input() value = '';
  /** Optional sentinel that always sits at the top of the panel — above the
   *  clear row and above the (possibly filtered) options — and is never
   *  filtered out by the search box. Used by the Team Explorer Teams filter
   *  to expose a "Direct Reportees" cut regardless of what the user types. */
  @Input() pinnedFirst = '';

  @Output() valueChange = new EventEmitter<string>();

  readonly open = signal(false);
  readonly query = signal('');
  private static nextId = 0;
  readonly searchInputId = `ss-search-${SearchableSelectComponent.nextId++}`;

  readonly filtered = computed(() => {
    const options = this.optionsSig();
    const q = this.query().trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.toLowerCase().includes(q));
  });

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  toggle(): void {
    if (this.open()) {
      this.close();
    } else {
      this.open.set(true);
      this.query.set('');
      // Focus the search box on the next frame so the user can type immediately.
      queueMicrotask(() => this.searchInput?.nativeElement.focus());
    }
  }

  close(): void {
    this.open.set(false);
  }

  select(v: string): void {
    if (v !== this.value) {
      this.value = v;
      this.valueChange.emit(v);
    }
    this.close();
  }

  /** Enter in the search box picks the first match, or clears if the input matches placeholder. */
  pickFirst(): void {
    const list = this.filtered();
    if (list.length > 0) this.select(list[0]);
  }

  /** Close on outside click so multiple selects don't stack open. */
  @HostListener('document:mousedown', ['$event'])
  onDocClick(ev: MouseEvent): void {
    if (!this.open()) return;
    if (!this.host.nativeElement.contains(ev.target as Node)) this.close();
  }
}
